import { MutableRefObject, useContext, useEffect, useState } from 'react';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import SecondaryButton from "../../ui/SecondaryButton";
import { ApplicableTableSettings } from "../../tableSettings";
import SmartTable from "../../uiControls/SmartTable";
import { JobNames } from "../../framework.core/JobNames";
import Date from "../../ui/Date";
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import TextBox from '../../ui/TextBox';
import Float from '../../ui/Float';
import ApplicationContext from '../../ApplicationContext';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { ApiResult, InsertResult } from '../../framework.core/data/ApiResult';
import { FloatRenderer, TextAlignLeftRenderer } from '../../uiControls/SmartTableRenderers';
import { compareIso, formatDate, formatDateTime, nowDate } from '../../dateUtils';
import { billInformationTable, profitDetailTable } from './tableSettings';
import InlineInputControlRowNoLabel from '../../ui/layout/InlineInputControlRowNoLabel';
import CheckBox from '../../ui/CheckBox';
import LeftRightSplit from '../../ui/LeftRightSplit';
import Label from '../../ui/Label';
import { ApHd } from '../../fms.core/ApHd';
import { getFmsGlobal } from '../../globals';
import { SQLInfo } from '../../framework.core/data/SQLInfo';
import { historyUpdate } from '../../fms.data/modules/FmsModFunc';
import { checkPropDirty, getSQLInfoList } from '../../fms.core/data/TrackingModule';
import { TrackableEntity } from '../../fms.core/data/ChangeTrackable';
import { updateStatusOP } from '../../fms.data/modules/FmsModAccount';
import useSetEditedId from '../../hooks/useSetEditedId';
import useDirtyCheck from '../../hooks/useDirtyCheck2';
import _ from 'lodash';

interface BillInformationViewStorageKeys {
  modelStorage: string,
};

interface BillInformationViewLoadedParams {
  billInformationView: ApHd,
  billInformationViewRef: MutableRefObject<ApHd | null>,
  setBillInformationView: (x: Partial<ApHd>) => void,
  onPopupClose?: () => void,
  save: () => Promise<void>,
};

function getBillInformationViewStorageKeys(billId: number): BillInformationViewStorageKeys {
  return { modelStorage: `bill-information-${billId}` }
}

function BillInformationViewImpl({
  billId,
  storageKey,
  onPopupClose,
  isView,
}: {
  billId: number,
  storageKey: string,
  onPopupClose: VoidFunction,
  isView: boolean,
}) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [initialized, setInitialized] = useState(false);
  const { modelStorage } = getBillInformationViewStorageKeys(billId);
  const setSavedEntity = useSetEditedId([JobNames.Bill, JobNames.GBill]);
  const [inStorage, setInStorage, removeFromStorage, storageRef] = useTypedFastStorage<ApHd>(modelStorage, null);
  const {setCleanState} = useDirtyCheck(
    storageKey,
    ['updated', 'updatedBy']);
    
  useEffect(() => {
    initialize();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function doClose() {
    cleanup();
    onPopupClose();
  }

  function cleanup() {
    removeFromStorage();
  }

  async function loadExisting(): Promise<ApHd | null> {
    const apiResult: ApiResult<ApHd> = await appContext.api.getBillInformationData(billId);
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (doClose) {
        doClose();
      }
    }

    return apiResult.result;
  }

  async function initialize() {
    const data = await loadExisting();
    //const data = new ApHd();
    if (data) {
      const loadedModelObj = new ApHd(_.cloneDeep(data));
      setCleanState(loadedModelObj);
      setInStorage(loadedModelObj);
    }

    if (storageRef.current !== null) {
      storageRef.current.trackable = true;
    }

    setInitialized(true);
  }

  if (!initialized || !inStorage) {
    return null;
  }

  async function checkDataAsync() {
    if (!storageRef.current) return false;

    if (storageRef.current.approved) {
      if(storageRef.current.appPaidDate === ''){
        await showGlobalOkDialog(<p>Invalid Scheduled date</p>);
        return false;
      }
      if (compareIso(formatDate(storageRef.current.billDate), formatDate(storageRef.current.appPaidDate)) === 1) {
        setInStorage({ appPaidDate: '' });
        await showGlobalOkDialog(<p>Scheduled date cannot be earlier than bill date</p>);
        return false;
      }
    }
    return true;
  }

  async function save() {
    if (!inStorage || !storageRef.current) {
      throw new Error('Invalid operation');
    }

    if (!storageRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      onPopupClose();
      return;
    }

    const valid = await checkDataAsync();
    if (!valid) {
      return;
    }

    const si: SQLInfo[] = [];

    let histMsgArray: string[] = [];
    if (checkPropDirty(storageRef.current, 'hold'))
      histMsgArray.push(storageRef.current.hold ? 'Hold' : 'Unhold');

    if (checkPropDirty(storageRef.current, 'approved'))
      histMsgArray.push(storageRef.current.approved ? 'Approved' : 'Cancel Approved');

    if (histMsgArray.length === 0) // 위의 2가지 이외의 값이 변경되었음을 의미
      histMsgArray.push('Bill Information EDIT');

    let histSqlInfo = historyUpdate("T_APHD", billId, appContext.user?.id ?? '', histMsgArray.join(', '));
    if (histSqlInfo) {
      si.push(histSqlInfo);
    }

    let updateStatueOp = updateStatusOP(JobNames.Bill, String(billId), inStorage.billReceived);
    if (updateStatueOp) {
      si.push(updateStatueOp);
    }

    const blStatusInfoList = getSQLInfoList(inStorage as TrackableEntity);
    blStatusInfoList.push(...si);

    const apiResult: ApiResult<InsertResult> = await appContext.api.saveMblStatusViewModel(blStatusInfoList);

    if (apiResult && apiResult.success) {
      await showGlobalOkDialog(<p>The data has been saved</p>);
      setSavedEntity(storageRef.current.id.toString(), false);
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult.message}</p>
      </div>);
    }

    onPopupClose();
  }

  return (
    <BillInformationViewInitializedImpl
      billInformationView={inStorage}
      billInformationViewRef={storageRef}
      setBillInformationView={setInStorage}
      onPopupClose={doClose}
      save={save}
    />
  );
}

function BillInformationViewInitializedImpl({
  billInformationView,
  billInformationViewRef,
  setBillInformationView,
  onPopupClose,
  save,
}: BillInformationViewLoadedParams) {
  const appContext = useContext(ApplicationContext);
  const fmsGlobal = getFmsGlobal();

  const [approveEnabled, setApproveEnabled] = useState(!billInformationView.hold && !fmsGlobal.BlockAPApprove);
  const [holdEnabled, setHoldEnabled] = useState(!billInformationView.approved && !fmsGlobal.BlockAPHold);

  const userID = appContext.user ? appContext.user.id ?? '' : '';

  const totalAmount = billInformationView.billDetail.map(x => x.amount).reduce((acc: number, x) => acc + x, 0);
  const totalMargin = billInformationView.apInfoDetailList.map(x => x.margin).reduce((acc: number, x) => acc + x, 0);

  function TotalAmountLabelRenderer() {
    return <div className="ifms-input-ctr-row2-label" style={{ textAlign: 'right', marginRight: '5px', marginTop: '2px', color: 'blue' }}>
      Total
    </div>;
  }

  function TotalMarginLabelRenderer() {
    return <div className="ifms-input-ctr-row2-label" style={{ textAlign: 'right', marginRight: '5px', marginTop: '2px', color: 'blue' }}>
      Total Margin
    </div>;
  }

  function TotalAmountRenderer() {
    return <div className='ifms-input-ctr-row2-label'><Float style={{ color: 'blue' }} decimals={2} noborder disabled dark value={totalAmount} /></div>;
  }

  function TotalMarginRenderer() {
    if (totalMargin < 0)
      return <div className='ifms-input-ctr-row2-label' ><Float style={{ color: 'red', fontWeight: 'bold' }} decimals={2} noborder disabled dark value={totalMargin} /></div>;
    else
      return <div className='ifms-input-ctr-row2-label' ><Float style={{ color: 'blue' }} decimals={2} noborder disabled dark value={totalMargin} /></div>;
  }

  const mainTableOptions: ApplicableTableSettings = {
    ...billInformationTable,
    selectable: true,
    // customTableId: 'billInformationTable',
    tableName: 'billInformationTable',
    data: billInformationView.billDetail,
    rowRenderers: [
      { accessor: 'billingCode', Cell: TextAlignLeftRenderer },
      { accessor: 'amount', Cell: FloatRenderer },
    ],
    footerRow: [
      {
        colspan: 1,
        renderer: TotalAmountLabelRenderer
      },
      {
        colspan: 1,
        renderer: TotalAmountRenderer
      },
    ],
  };

  const billingDetailTableOptions: ApplicableTableSettings = {
    ...profitDetailTable,
    selectable: true,
    tableName: 'profitDetailTable',
    data: billInformationView.apInfoDetailList,
    rowRenderers: [
      { accessor: 'billingCode', Cell: TextAlignLeftRenderer },
      { accessor: 'revenue', Cell: FloatRenderer },
      { accessor: 'cost', Cell: FloatRenderer },
      { accessor: 'crDbAmount', Cell: FloatRenderer },
      { accessor: 'margin', Cell: FloatRenderer },
    ],
    footerRow: [
      {
        colspan: 4,
        renderer: TotalMarginLabelRenderer
      },
      {
        colspan: 1,
        renderer: TotalMarginRenderer
      },
    ],
    getCustomCellProps: (currentProps: any, cell: any, rowIndex: any) => {
      if (
        cell.column.id === 'margin') {
        if (cell.row.original.margin < 0) {
          currentProps.style.color = 'red';
          currentProps.style.fontWeight = 'bold';
        }
      }
      return currentProps;
    },
  };

  return (
    <div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
      <div>
        <div>
          <div style={{ width: '135px' }}>
            <InlineInputControlRow mode='w60' label="Bill Date" >
              <Date value={billInformationView.billDate} disabled />
            </InlineInputControlRow>
          </div>

          <div style={{ display: 'flex' }}>
            <InlineInputControlRowNoLabel>
              <LeftRightSplit
                leftSize="60px"
                rightSize="calc(100% - 60px)"
                left={
                  <div style={{ marginLeft: '15px', position: 'relative', top: '-2px' }}>
                    <CheckBox
                    id='billInformation-received-check'
                    name = "billInformation-received"
                      value={billInformationView.billReceived}
                      onChange={(billReceived) => {
                        setBillInformationView({ billReceived })
                      }}
                    />
                  </div>
                }
                right={
                  <>Bill Received</>
                }
              />
            </InlineInputControlRowNoLabel>
          </div>

          <div style={{ display: 'flex' }}>
            <div style={{ width: '100px' }}>
              <InlineInputControlRowNoLabel>
                <LeftRightSplit
                  leftSize="60px"
                  rightSize="calc(100% - 60px)"
                  left={
                    <div style={{ marginLeft: '15px', position: 'relative', top: '-2px' }}>
                      <CheckBox
                       id='billInformation-hold-check'
                       name = "billInformation-hold"
                        value={billInformationView.hold}
                        onChange={(hold) => {
                          if (hold) {
                            if (fmsGlobal.BlockAPApprove === false)
                              setApproveEnabled(false);

                            setBillInformationView({ hold: true, holdUser: userID })
                          }
                          else {
                            if (fmsGlobal.BlockAPApprove === false)
                              setApproveEnabled(true);

                            setBillInformationView({ hold: false, holdReason: '', holdUser: '' })
                          }
                        }}
                        disabled={!holdEnabled}
                      />
                    </div>
                  }
                  right={
                    <>Hold</>
                  }
                />
              </InlineInputControlRowNoLabel>
            </div>

            <div style={{ marginLeft: '60px', width: '330px' }}>
              <InlineInputControlRow mode='w60' label="Reason">
                <div style={{ width: '260px' }}>
                  <TextBox
                    value={billInformationView.holdReason}
                    disabled={!billInformationView.hold}
                    onChange={(e) => { setBillInformationView({ holdReason: e.target.value }) }}
                    maxLength={30}
                  />
                </div>
              </InlineInputControlRow>
            </div>

            <div style={{ marginLeft: '40px', width: '120px' }}>
              <InlineInputControlRow mode='w40' label="By">
                <TextBox value={billInformationView.holdUser} disabled />
              </InlineInputControlRow>
            </div>
          </div>

          <div style={{ display: 'flex' }}>
            <div style={{ width: '100px' }}>
              <InlineInputControlRowNoLabel>
                <LeftRightSplit
                  leftSize="60px"
                  rightSize="calc(100% - 60px)"
                  left={
                    <div style={{ marginLeft: '15px', position: 'relative', top: '-2px' }}>
                      <CheckBox
                      id='billInformation-general-check'
                      name = "billInformation-general"
                        value={billInformationView.remarksYN}
                        onChange={(remarksYN) => {
                          if (remarksYN)
                            setBillInformationView({ remarksYN, remarkUser: userID })
                          else
                            setBillInformationView({ remarksYN, remarkUser: '', remarks: '' })
                        }}
                      />
                    </div>
                  }
                  right={
                    <>General</>
                  }
                />
              </InlineInputControlRowNoLabel>
            </div>

            <div style={{ marginLeft: '60px', width: '330px' }}>
              <InlineInputControlRow mode='w60' label="Remark">
                <div style={{ width: '260px' }}>
                  <TextBox
                    value={billInformationView.remarks}
                    disabled={!billInformationView.remarksYN}
                    onChange={(e) => { setBillInformationView({ remarks: e.target.value }) }}
                    maxLength={30}
                  />
                </div>
              </InlineInputControlRow>
            </div>

            <div style={{ marginLeft: '40px', width: '120px' }}>
              <InlineInputControlRow mode='w40' label="By">
                <TextBox value={billInformationView.remarkUser} disabled />
              </InlineInputControlRow>
            </div>

          </div>

          <div style={{ display: 'flex' }}>
            <div style={{ width: '100px' }}>
              <InlineInputControlRowNoLabel>
                <LeftRightSplit
                  leftSize="60px"
                  rightSize="calc(100% - 60px)"
                  left={
                    <div style={{ marginLeft: '15px', position: 'relative', top: '-2px' }}>
                      <CheckBox
                       id='billInformation-approved-check'
                       name = "billInformation-approved"
                        value={billInformationView.approved}
                        onChange={(approved) => {
                          if (approved) {
                            const appPaidDate = compareIso(formatDate(billInformationView?.billDate), nowDate()) > 0 ? formatDate(billInformationView?.billDate) : nowDate();
                            if (fmsGlobal.BlockAPHold === false) {
                              setHoldEnabled(false);
                            }
                            setBillInformationView({ approved, approvedUser: userID, appPaidDate });
                          }
                          else {
                            if (fmsGlobal.BlockAPHold === false) {
                              setHoldEnabled(true);
                            }
                            setBillInformationView({ approved, approvedUser: '', appPaidDate: null });
                          }
                        }}
                        disabled={!approveEnabled}
                      />
                    </div>
                  }
                  right={
                    <>Approved</>
                  }
                />
              </InlineInputControlRowNoLabel>
            </div>

            <div style={{ marginLeft: '60px', width: '330px' }}>
              <InlineInputControlRow mode='w100' label="Scheduled Date">
                <div style={{ width: '80px' }}>
                  <Date
                    value={billInformationView.appPaidDate}
                    disabled={!billInformationView.approved}
                    onChange={(appPaidDate) => {
                      setBillInformationView({ appPaidDate})
                    }}
                  />
                </div>
              </InlineInputControlRow>
            </div>

            <div style={{ marginLeft: '40px', width: '120px' }}>
              <InlineInputControlRow mode='w40' label="By">
                <TextBox
                  value={billInformationView.approvedUser}
                  disabled
                />
              </InlineInputControlRow>
            </div>
          </div>
        </div>

        <div>
          <div>
            <Label label='Bill Information'></Label>
            <Label style={{ marginLeft: '155px' }} label='Profit Detail'></Label>
          </div>
          <div style={{ display: 'flex' }}>
            <div style={{ width: '210px' }}>
              {/* <SmartTable {...mainTableOptions} selectedRowIndex={billInformationView.apInfoDetailList.length - 1} /> */}
              <SmartTable {...mainTableOptions} />
            </div>

            <div style={{ width: '390px', marginLeft: '20px' }}>
              <SmartTable {...billingDetailTableOptions} />
            </div>
          </div>
        </div>

        <div style={{ width: '240px', marginTop: '10px' }}>
          <InlineInputControlRow mode="w80" label="Last Update" >
            <LeftRightSplit
              leftSize="calc(100% - 60px)"
              rightSize="60px"
              left={
                <TextBox value={formatDateTime(billInformationView.u2Date)} disabled />
              }
              right={
                <TextBox value={billInformationView.u2Id} disabled />
              }
            />
          </InlineInputControlRow>
        </div>
      </div>

      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          style={{ width: '90px' }}
          onClick={save}>
          Apply
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          style={{ width: '90px' }}
          onClick={onPopupClose}>
          Cancel
        </SecondaryButton>
      </div>
    </div>
  );
}

const BillInformation = ({
  billId,
  storageKey,
  onPopupClose,
  isView,
}: {
  billId: number,
  storageKey: string,
  onPopupClose: VoidFunction,
  isView: boolean,
}) => {
  return (
    <BillInformationViewImpl billId={billId} key={storageKey} storageKey={storageKey} onPopupClose={onPopupClose} isView={isView} />
  );
};

export default BillInformation;